import CustomField from "components/common/CustomField";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import FormButtons from "components/common/FormButtons";
import SingleFileUpload from "components/common/SingleFileUpload";
import Spinners from "components/common/Spinners";
import { introductionSchema } from "components/common/Validation";
import { Form, Formik, FieldArray, ErrorMessage } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getGlobalSettings,
  updateIntroPageDetails,
} from "store/actions/globalSettingSlice";
import { showAlert } from "store/reducers/alertReducer";

const initialValues = {
  introScreens: [
    {
      name: "",
      nameAr: "",
      description: "",
      descriptionAr: "",
      imageUrl: "",
      imageUrlAr: "",
    },
  ],
};

const IntroductionPage = () => {
  const { t } = useTranslation();
  document.title = t("Introduction Page | Work Space");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, loading, globalSettings } = useSelector(
    (state) => state.GlobalSettings
  );

  const [formValues, setFormValues] = useState(initialValues);

  useEffect(() => {
    const getDetails = () => {
      dispatch(getGlobalSettings());
    };

    getDetails();
  }, []);

  useEffect(() => {
    if (globalSettings.length > 0) {
      const { introScreens } = globalSettings[0] || {};

      // Update formValues using destructured values
      setFormValues({ introScreens: introScreens });
    }
  }, [globalSettings]);

  const handleOnSubmit = (values) => {
    let data = new FormData();

    data.append(`id`, globalSettings[0]?._id);

    values?.introScreens?.forEach((screen, index) => {
      data.append(`introScreens[${index}][name]`, screen.name);
      data.append(`introScreens[${index}][nameAr]`, screen.nameAr);
      data.append(`introScreens[${index}][description]`, screen.description);
      data.append(
        `introScreens[${index}][descriptionAr]`,
        screen.descriptionAr
      );
      data.append(`introScreens[${index}][image]`, screen.imageUrl);
      data.append(`introScreens[${index}][imageAr]`, screen.imageUrlAr);
    });

    dispatch(updateIntroPageDetails(data))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          navigate(-1);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <div className="page-content common_page">
      {loading ? (
        <Spinners />
      ) : (
        <Container fluid>
          <Row>
            <DetailsPageTitle title={t("Introduction Page")} />
            <Formik
              initialValues={formValues}
              enableReinitialize={true}
              validationSchema={introductionSchema}
              onSubmit={handleOnSubmit}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <div className="common_wrapper">
                    <FieldArray name="introScreens">
                      {({ push, remove }) => (
                        <>
                          {values.introScreens.map((_, index) => (
                            <Row key={index} className=" mb-3">
                              <Col lg={6}>
                                <SingleFileUpload
                                  uploadedFile={
                                    values.introScreens[index].imageUrl || null
                                  }
                                  setUploadedFile={(file) =>
                                    setFieldValue(
                                      `introScreens.${index}.imageUrl`,
                                      file
                                    )
                                  }
                                  maxFileSize={0.3 * 1024 * 1024} // 300 KB
                                  acceptedFileTypes={[
                                    "image/png",
                                    "image/jpeg",
                                    "image/svg+xml",
                                  ]}
                                  className=""
                                  isShowErrorMsg={true}
                                  name={`introScreens.${index}.imageUrl`}
                                />
                              </Col>
                              <Col lg={6}>
                                <SingleFileUpload
                                  uploadedFile={
                                    values.introScreens[index].imageUrlAr ||
                                    null
                                  }
                                  setUploadedFile={(file) =>
                                    setFieldValue(
                                      `introScreens.${index}.imageUrlAr`,
                                      file
                                    )
                                  }
                                  maxFileSize={0.3 * 1024 * 1024} // 300 KB
                                  acceptedFileTypes={[
                                    "image/png",
                                    "image/jpeg",
                                    "image/svg+xml",
                                  ]}
                                  className=""
                                  isShowErrorMsg={true}
                                  name={`introScreens.${index}.imageUrlAr`}
                                />
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomField
                                    name={`introScreens.${index}.name`}
                                    type="text"
                                    placeholder={t("Enter English Title")}
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomField
                                    name={`introScreens.${index}.nameAr`}
                                    type="text"
                                    placeholder={t("ادخل عنوان")}
                                    isArabic={true}
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomField
                                    name={`introScreens.${index}.description`}
                                    placeholder="Enter English Description"
                                    as="textarea"
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomField
                                    name={`introScreens.${index}.descriptionAr`}
                                    placeholder="ادخل وصف"
                                    as="textarea"
                                    isArabic={true}
                                  />
                                </div>
                              </Col>
                              {values.introScreens.length > 1 && (
                                <Col lg={12} className="text-end">
                                  <Button
                                    variant="danger"
                                    onClick={() => remove(index)}
                                  >
                                    {t("Remove")}
                                  </Button>
                                </Col>
                              )}
                            </Row>
                          ))}
                          <Row className="mb-3">
                            <Col lg={12}>
                              <button
                                className="main_btn d-block ms-auto"
                                onClick={() =>
                                  push({
                                    name: "",
                                    nameAr: "",
                                    description: "",
                                    descriptionAr: "",
                                    imageUrl: "",
                                    imageUrlAr: "",
                                  })
                                }
                              >
                                {t("Add More")}
                              </button>
                            </Col>
                          </Row>
                        </>
                      )}
                    </FieldArray>
                  </div>
                  <Row className="px-0 my-4">
                    <FormButtons
                      addBtnText={t("Update")}
                      isLoading={isLoading}
                    />
                  </Row>
                </Form>
              )}
            </Formik>
          </Row>
        </Container>
      )}
    </div>
  );
};

export default IntroductionPage;
